import { z } from 'zod';

/**
 * A share link over a snapshot of a job's files.
 *
 * The snapshot is fixed at creation: files added to the job afterwards never
 * appear in an existing share, and a file deleted from the library drops out of
 * every share that held it.
 *
 * `label` is office-facing only — it is what makes two links on the same job
 * tellable apart in the manage list, and the client never sees it.
 */
export const fileShareSchema = z.object({
  id: z.string().uuid(),
  token: z.string().min(1),
  label: z.string().nullable(),
  fileCount: z.number().int().nonnegative(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
});
export type FileShare = z.infer<typeof fileShareSchema>;

/** How long a label may be. Long enough for "Kitchen — before photos for Dana". */
export const MAX_FILE_SHARE_LABEL = 120;

/**
 * How many files one link may carry.
 *
 * The ceiling is the zip, not the database: every file in a share is streamed
 * through one response when the client hits Download all.
 */
export const MAX_FILE_SHARE_FILES = 200;

export const createFileShareSchema = z.object({
  fileIds: z.array(z.string().uuid()).min(1).max(MAX_FILE_SHARE_FILES),
  label: z.string().trim().max(MAX_FILE_SHARE_LABEL).optional(),
});
export type CreateFileShareInput = z.infer<typeof createFileShareSchema>;

/**
 * One file as the public gallery sees it.
 *
 * Deliberately thinner than a JobFileEntry: no R2 keys, no folder, no tags, no
 * uploader. The client is shown a name, a size and enough type information to
 * pick an icon — everything else is internal.
 */
export const sharedFileSchema = z.object({
  id: z.string().uuid(),
  fileName: z.string().min(1),
  contentType: z.string().nullable(),
  sizeBytes: z.number().int().nonnegative().nullable(),
  isImage: z.boolean(),
});
export type SharedFile = z.infer<typeof sharedFileSchema>;

/** The public gallery payload. `address` is the job's service address, or null. */
export const sharedFileSetSchema = z.object({
  label: z.string().nullable(),
  address: z.string().nullable(),
  files: z.array(sharedFileSchema),
});
export type SharedFileSet = z.infer<typeof sharedFileSetSchema>;
